import React, { useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dropdown,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  ListDivider,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalDialog,
} from '@mui/joy';
import { FaEllipsisV } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { IconWrapper } from '../../../components/IconWrapper';
import { useSupabaseAuth } from '../../../contexts/SupabaseAuthContext';
import { toFreeAgentInvoiceAppUrl } from '../../../freeagent/appUrl';
import {
  allowedInvoiceActions,
  canChangeInvoiceRef,
  normaliseInvoiceStatus,
  validateInvoiceRef,
  type AccountInvoiceStatus,
} from '../accountInvoice.model';
import { accountInvoicesService } from '../accountInvoices.service';
import { SendInvoiceModal } from './SendInvoiceModal';

export type InvoiceMenuRow = {
  id: string;
  status: string;
  invoiceRef: string;
  freeagentInvoiceUrl?: string | null;
};

/** Invoice page route for account invoices. */
export function accountInvoicePath(id: string): string {
  return `/invoicing/invoices/${id}`;
}

/** Row menu for an account invoice: open, PDF, status changes, number override and delete. */
export function InvoiceActionsMenu({
  invoice,
  onChanged,
  onDeleted,
  hideOpen,
}: {
  invoice: InvoiceMenuRow;
  onChanged: () => void;
  onDeleted?: () => void;
  hideOpen?: boolean;
}) {
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const status = normaliseInvoiceStatus(invoice.status);
  const allowed = allowedInvoiceActions(status);
  const refEditable = canChangeInvoiceRef(status, invoice.freeagentInvoiceUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refOpen, setRefOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [refValue, setRefValue] = useState(invoice.invoiceRef);

  const run = async (action: () => Promise<unknown>, after: () => void = onChanged) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      after();
    } catch (err) {
      setError(err instanceof Error ? err.message : String((err as any)?.message || err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = (next: AccountInvoiceStatus, confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return;
    void run(() => accountInvoicesService.setStatus(invoice.id, next));
  };

  return (
    <>
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{ root: { variant: 'plain', color: 'neutral', size: 'sm', disabled: busy, 'aria-label': `Actions for ${invoice.invoiceRef}` } as any }}
          onClick={(event) => event.stopPropagation()}
        >
          <IconWrapper icon={FaEllipsisV} />
        </MenuButton>
        <Menu placement="bottom-end" size="sm" onClick={(event) => event.stopPropagation()}>
          {!hideOpen && <MenuItem onClick={() => navigate(accountInvoicePath(invoice.id))}>Open</MenuItem>}
          {!hideOpen && status !== 'void' && (
            <MenuItem onClick={() => setSendOpen(true)}>Send to customer…</MenuItem>
          )}
          <MenuItem onClick={() => run(async () => accountInvoicesService.downloadPdf(await accountInvoicesService.load(invoice.id)), () => undefined)}>
            Download PDF
          </MenuItem>
          {refEditable && (
            <MenuItem onClick={() => { setRefValue(invoice.invoiceRef); setRefOpen(true); }}>Change number…</MenuItem>
          )}
          {invoice.freeagentInvoiceUrl ? (
            <MenuItem onClick={() => window.open(toFreeAgentInvoiceAppUrl(invoice.freeagentInvoiceUrl), '_blank', 'noopener')}>
              Open in FreeAgent
            </MenuItem>
          ) : status !== 'void' && (
            <MenuItem
              onClick={() => {
                if (!window.confirm(`Send ${invoice.invoiceRef} to FreeAgent? It is created there as an issued invoice.`)) return;
                void run(async () => {
                  const result = await accountInvoicesService.sendToFreeAgent(invoice.id, user?.id);
                  if (!result.success) throw new Error(result.error || 'FreeAgent did not accept the invoice.');
                });
              }}
            >
              Send to FreeAgent
            </MenuItem>
          )}
          {(allowed.markSent || allowed.markPaid || allowed.void) && <ListDivider />}
          {allowed.markSent && (
            <MenuItem
              onClick={() => setStatus('sent', invoice.freeagentInvoiceUrl
                ? `Mark ${invoice.invoiceRef} as sent? It is already in FreeAgent, so nothing new is created there.`
                : `Mark ${invoice.invoiceRef} as sent? If FreeAgent sync is on, it is created in FreeAgent now. If you already raised it in FreeAgent by hand, cancel, or it may be duplicated.`)}
            >
              Mark as sent
            </MenuItem>
          )}
          {allowed.markPaid && <MenuItem onClick={() => setStatus('paid')}>Mark as paid</MenuItem>}
          {allowed.void && (
            <MenuItem color="danger" onClick={() => setStatus('void', `Void ${invoice.invoiceRef}? It no longer counts as invoiced, and a recurring period becomes due again.`)}>
              Void
            </MenuItem>
          )}
          {allowed.delete && (
            <>
              <ListDivider />
              <MenuItem
                color="danger"
                onClick={() => {
                  if (!window.confirm(`Delete draft ${invoice.invoiceRef}? A recurring period becomes due again.`)) return;
                  void run(() => accountInvoicesService.remove(invoice.id), onDeleted || onChanged);
                }}
              >
                Delete draft
              </MenuItem>
            </>
          )}
        </Menu>
      </Dropdown>

      {sendOpen && (
        <SendInvoiceModal
          invoiceId={invoice.id}
          open={sendOpen}
          onClose={() => setSendOpen(false)}
          onSent={(text) => {
            setSendOpen(false);
            setNotice(text);
            onChanged();
          }}
        />
      )}

      <Modal open={!!notice} onClose={() => setNotice(null)}>
        <ModalDialog size="sm">
          <DialogTitle>{invoice.invoiceRef}</DialogTitle>
          <DialogContent><Alert color="success">{notice}</Alert></DialogContent>
          <DialogActions><Button onClick={() => setNotice(null)}>OK</Button></DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={!!error} onClose={() => setError(null)}>
        <ModalDialog size="sm">
          <DialogTitle>{invoice.invoiceRef}</DialogTitle>
          <DialogContent><Alert color="danger">{error}</Alert></DialogContent>
          <DialogActions><Button onClick={() => setError(null)}>OK</Button></DialogActions>
        </ModalDialog>
      </Modal>

      <Modal open={refOpen} onClose={() => !busy && setRefOpen(false)}>
        <ModalDialog sx={{ width: { xs: '100%', sm: 420 } }}>
          <DialogTitle>Change invoice number</DialogTitle>
          <DialogContent>
            <FormControl error={!!validateInvoiceRef(refValue)}>
              <FormLabel>Invoice number</FormLabel>
              <Input value={refValue} onChange={(event) => setRefValue(event.target.value)} autoFocus />
              <FormHelperText>
                {validateInvoiceRef(refValue) || 'Must not be used by another invoice. Fixed once it is paid or in FreeAgent.'}
              </FormHelperText>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              loading={busy}
              disabled={!!validateInvoiceRef(refValue) || refValue.trim() === invoice.invoiceRef}
              onClick={() => run(() => accountInvoicesService.changeRef(invoice.id, refValue), () => { setRefOpen(false); onChanged(); })}
            >
              Save
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setRefOpen(false)} disabled={busy}>Cancel</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
}
